import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, CheckSquare, Dumbbell, Activity, ChevronRight } from 'lucide-react';
import { useMemberPortal } from './MemberPortal';
import { getMyMemberships, getMyAttendances, getMyPtAssignments } from '../../lib/memberDb';
import { getPtSessionsForMember } from '../../lib/ptSessions';
import { daysUntilExpiry } from '../../utils/helpers';
import { format, parseISO, isAfter } from 'date-fns';
import { ko } from 'date-fns/locale';

export default function MyHome() {
  const { memberRecord, gym } = useMemberPortal();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getMyMemberships(memberRecord.id),
      getMyAttendances(memberRecord.id),
      getMyPtAssignments(memberRecord.id),
      getPtSessionsForMember(),
    ]).then(([m, a, pt, s]) => {
      setMemberships(m);
      setAttendances(a);
      setAssignments(pt);
      setSessions(s);
    }).finally(() => setLoading(false));
  }, [memberRecord.id]);

  const activeMembership = memberships.find(m => m.status === 'active');
  const expiryDays = activeMembership ? daysUntilExpiry(activeMembership.endDate) : null;

  const thisMonthStr = format(new Date(), 'yyyy-MM');
  const thisMonthCount = attendances.filter(a => a.date?.startsWith(thisMonthStr)).length;

  const activePt = assignments.filter(a => a.status === 'active');
  const ptRemaining = activePt.reduce((sum, a) => sum + (a.totalSessions - a.usedSessions), 0);

  const nextSession = sessions
    .filter(s => s.status === 'scheduled' && isAfter(parseISO(s.scheduledAt), new Date()))
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))[0];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? '좋은 아침이에요' : hour < 18 ? '안녕하세요' : '좋은 저녁이에요';

  if (loading) {
    return (
      <div className="flex justify-center items-center pt-20">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* 인사말 */}
      <div className="pt-1">
        <p className="text-xs text-gray-400 dark:text-gray-500">{greeting}</p>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">{memberRecord.name}님</h1>
        {gym?.name && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{gym.name}</p>
        )}
      </div>

      {/* 회원권 배너 */}
      {activeMembership ? (
        <div className={`rounded-xl p-4 border ${
          expiryDays !== null && expiryDays <= 3
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : expiryDays !== null && expiryDays <= 7
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">이용중인 회원권</p>
              <p className="font-semibold text-gray-900 dark:text-white mt-0.5">{activeMembership.productName}</p>
              <p className="text-xs text-gray-400 mt-1">{activeMembership.endDate} 까지</p>
            </div>
            <div className="text-right">
              {expiryDays !== null && expiryDays >= 0 ? (
                <p className={`text-3xl font-bold ${
                  expiryDays <= 3 ? 'text-red-600 dark:text-red-400' :
                  expiryDays <= 7 ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-primary-600 dark:text-primary-400'
                }`}>
                  D-{expiryDays}
                </p>
              ) : (
                <p className="text-sm font-semibold text-red-500">만료됨</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-4 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">이용중인 회원권이 없습니다</p>
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => navigate('/my/attendance')}
          className="card p-3 text-center active:scale-95 transition-transform"
        >
          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{thisMonthCount}</p>
          <p className="text-xs text-gray-400 mt-0.5">이번달 출석</p>
        </button>
        <button
          onClick={() => navigate('/my/pt')}
          className="card p-3 text-center active:scale-95 transition-transform"
        >
          <p className={`text-2xl font-bold ${
            ptRemaining === 0 ? 'text-gray-400' :
            ptRemaining <= 3 ? 'text-red-500' :
            ptRemaining <= 10 ? 'text-yellow-500' :
            'text-green-500'
          }`}>
            {ptRemaining}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">PT 잔여</p>
        </button>
        <button
          onClick={() => navigate('/my/attendance')}
          className="card p-3 text-center active:scale-95 transition-transform"
        >
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{attendances.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">총 출석</p>
        </button>
      </div>

      {/* 다음 PT 일정 */}
      {nextSession && (
        <button
          onClick={() => navigate('/my/pt')}
          className="w-full card p-4 flex items-center justify-between active:scale-95 transition-transform"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <Dumbbell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="text-left">
              <p className="text-xs text-gray-400 dark:text-gray-500">다음 PT 일정</p>
              <p className="font-semibold text-gray-900 dark:text-white text-sm mt-0.5">
                {format(parseISO(nextSession.scheduledAt), 'M월 d일 (EEE) HH:mm', { locale: ko })}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{nextSession.trainerName} 트레이너</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
        </button>
      )}

      {/* 바로가기 */}
      <div>
        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-2">바로가기</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { icon: QrCode, label: 'QR 출석', path: '/my/qr', color: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20' },
            { icon: CheckSquare, label: '출석 기록', path: '/my/attendance', color: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' },
            { icon: Dumbbell, label: 'PT 일정', path: '/my/pt', color: 'text-green-500 bg-green-50 dark:bg-green-900/20' },
            { icon: Activity, label: '신체 기록', path: '/my/body', color: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20' },
          ].map(({ icon: Icon, label, path, color }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className="card p-4 flex items-center gap-3 active:scale-95 transition-transform"
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
